<script>
import VerifyEmail from "./VerifyEmail";
import Modal from "@/components/_Shared/Modal";

import addressMixin from "@/mixins/addressMixin";
import dateMixin from "@/mixins/dateMixin";
import numberMixin from "@/mixins/numberMixin";
import scrollToMixin from "@/mixins/scrollToMixin";
import accountMixin from "@/mixins/accountMixin";

import { mapState, mapGetters, mapActions } from "vuex";

export default {
  mixins: [addressMixin, dateMixin, numberMixin, scrollToMixin, accountMixin],

  components: {
    Modal,
    VerifyEmail
  },

  data() {
    return {
      preloader: false,
      show_verify_email: false,
      confirmed: false,
      error: false
    };
  },

  computed: {
    ...mapState("newOrder", [
      "day",
      "professional",
      "information",
      "address",
      "price",
      "additional_informations",
      "use_my_products",
      "user",
      "orderToRebook"
    ]),
    ...mapGetters("newOrder", ["labelDuration", "duration"]),
    ...mapGetters("user", ["full_name"])
  },

  methods: {
    ...mapActions("newOrder", ["sendToCart", "create", "rebookOrReschedule"]),

    ...mapActions("user", ["createPaymentGateways"]),

    async submit() {
      this.preloader = true;

      try {
        let account =
          this.user.type === "accounts" ? this.user : this.user.account;

        if (account.status != "active") this.verifyUser();
        else if (this.orderToRebook) await this.sendRebookOrReschedule();
        else await this.createOrder();
      } catch (e) {
        this.$toast.error(e);
        this.preloader = false;
      }
    },

    async createOrder() {
      try {
        await this.create();
        this.$emit("created");
      } catch (e) {
        this.$toast.error(e);
      }
      this.preloader = false;
    },

    async sendRebookOrReschedule() {
      try {
        await this.rebookOrReschedule();
        this.$emit("rebooked");
      } catch (e) {
        this.$toast.error(e);
      }
      this.preloader = false;
    },

    async verifyUser() {
      try {
        if (this.user.status === "waiting_email_verification") {
          this.show_verify_email = true;
          this.preloader = false;
          return;
        } else if (this.user.status === "email_verified") {
          this.show_verify_email = false;
          await this.createPaymentGateways();
        } else {
          throw "Verifique o status do cliente";
        }
      } catch (e) {
        throw e;
      }
    }
  },

  watch: {
    user() {
      this.submit();
    }
  }
};
</script>

<template>
  <div id="orders" class="section">
    <div class="section-title">
      CONFIRME SEU PEDIDO
      <hr />
    </div>

    <div class="details mb-20" :class="{ confirmed: confirmed }">
      <div class="row">
        <div class="column flex-1">
          <b>{{ user.account ? user.account : user | fullName }}</b>
          <span>Dia: {{ day | formatDate("DD/MM/Y") }}</span>
          <span>Horário: {{ labelDuration }}</span>
          <span
            >Duração: {{ duration }} {{ duration > 1 ? "horas" : "hora" }}</span
          >
        </div>

        <div class="column flex-1">
          <b>Local</b>
          <span>{{ address.name }}</span>
          <span>{{ address | fullAddress }}</span>
        </div>

        <div class="column flex-1">
          <b>Limpeza</b>
          <span>
            Profissional: {{ professional.account.first_name }}
            {{ professional.account.last_name }}
          </span>
          <span>Valor: {{ price | intToCurrency }}</span>
          <span>
            Produtos:
            {{
              use_my_products
                ? "Irei usar meus produtos"
                : "A diarista levará os produtos"
            }}
          </span>
        </div>
      </div>

      <div class="informations mt-20">
        Informações:
        <p>{{ additional_informations }}</p>
      </div>

      <div class="d-flex justify-center">
        <div
          class="btn"
          :class="{ 'bg-pink-outline': confirmed, 'bg-pink': !confirmed }"
          @click="
            confirmed = true;
            scrollTo('#finalize', -300);
          "
        >
          <span v-if="confirmed">confirmado</span>
          <span v-else>confirmar dados</span>
        </div>
      </div>
    </div>

    <transition name="fade" mode="out-in">
      <div id="finalize" v-if="confirmed" class="d-flex justify-center mt-30">
        <div
          class="btn bg-green"
          :class="{ 'btn--loading': preloader }"
          @click="submit"
        >
          <span v-if="orderToRebook">reagendar pedido</span>
          <span v-else>finalizar compra</span>
        </div>
      </div>
    </transition>

    <transition name="fade" mode="out-in">
      <modal
        title="OCORREU UM ERRO AO CRIAR O PEDIDO"
        v-if="error"
        size="md"
        @close="$emit('close')"
      >
        <template slot="body">
          <div v-html="error"></div>
        </template>

        <template slot="footer">
          <button class="btn bg-purple-outline" @click="error = false">
            fechar
          </button>
        </template>
      </modal>
    </transition>

    <transition name="zoom" mode="out-in">
      <verify-email
        v-if="show_verify_email"
        @close="show_verify_email = false"
      />
    </transition>
  </div>
</template>

<style scoped>
.d-flex {
  flex-direction: column;
  align-items: center;
}

.section {
  margin-bottom: 0;
}

.details {
  width: 460px;
  margin-top: 20px;
  font-size: 13px;
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 20px;
  transition: all 0.24s ease;
}

.details.confirmed {
  border: 1px solid var(--pink);
}

.details .row .column {
  flex: 1;
  align-items: flex-start;
  margin: 10px;
  text-align: left;
}

.details .row .column:first-child {
  margin-left: 0;
}

.details .row .column:last-child {
  margin-right: 0;
}

.details .row .column span {
  margin: 2px 0;
}

.details .btn {
  margin-bottom: -40px;
}

.informations {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.informations p {
  word-break: break-word;
  margin-left: 20px;
  border-left: 1px solid #ddd;
  padding-left: 10px;
}

@media (max-width: 630px) {
  .row {
    flex-direction: column !important;
  }

  .column {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .details {
    width: 300px;
  }
}

@media (max-width: 470px) {
  .details {
    width: 200px;
  }
}

@media (max-width: 365px) {
  .details {
    width: 160px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/PurchaseFlow/Steps/Confirmation.vue